import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Shield, FileText, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

/**
 * PolicyConsentGate — shown after login when user hasn't accepted the latest policy versions.
 * Blocks platform usage until acceptance.
 */
export function PolicyConsentGate({ children }: { children: React.ReactNode }) {
  const { user, role } = useAuth();
  const [pendingPolicies, setPendingPolicies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    checkPolicies();
  }, [user]);

  const checkPolicies = async () => {
    try {
      // Get all active policies
      const { data: activePolicies } = await supabase
        .from("legal_policies")
        .select("id, policy_type, version, title, content")
        .eq("is_active", true);
      
      if (!activePolicies?.length) { setLoading(false); return; }

      // Get user's acceptances
      const { data: acceptances } = await supabase
        .from("policy_acceptances")
        .select("policy_id")
        .eq("user_id", user!.id);
      
      const acceptedIds = new Set((acceptances || []).map((a: any) => a.policy_id));
      
      // Find policies not yet accepted
      const pending = activePolicies.filter((p: any) => !acceptedIds.has(p.id));
      setPendingPolicies(pending);
    } finally {
      setLoading(false);
    }
  };

  const acceptPolicy = async (policy: any) => {
    setAccepting(true);
    try {
      const { error } = await supabase.from("policy_acceptances").insert({
        user_id: user!.id,
        policy_id: policy.id,
        policy_type: policy.policy_type,
        policy_version: policy.version,
        user_agent: navigator.userAgent,
      });
      if (error) throw error;
      
      if (currentIndex < pendingPolicies.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setPendingPolicies([]);
      }
    } catch (err: any) {
      toast.error("Failed to record acceptance: " + err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return null;
  if (!pendingPolicies.length) return <>{children}</>;

  const current = pendingPolicies[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Policy Update Required</h2>
              <p className="text-xs text-muted-foreground">
                Please review and accept the updated {current.title} ({currentIndex + 1} of {pendingPolicies.length})
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_p]:text-foreground/80 [&_li]:text-foreground/80">
            <ReactMarkdown>{current.content}</ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/50 bg-card/50">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              By accepting, you agree to abide by these terms. Version {current.version}.
            </p>
            <button
              onClick={() => acceptPolicy(current)}
              disabled={accepting}
              className="flex items-center gap-2 btn-gradient text-primary-foreground px-6 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
            >
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              I Accept
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
